import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { 
  SubscriptionTier, 
  SUBSCRIPTION_TIERS, 
  getTierByProductId,
  getTierFeatures,
  type KreativpaketAccess,
} from '@/config/subscriptionTiers';
import { isRealMember as computeIsRealMember } from '@/lib/access/membership';

// Tier source indicates how the user obtained their tier
// - 'stripe': Active Stripe subscription (real paying member)
// - 'guest': Invited via Team/Gemeinsam lernen (real member)
// - 'manual': Admin manually set tier (real member)
// - 'training': Fachkraft program (NOT a real member - temporary pro access)
export type TierSource = 'stripe' | 'guest' | 'manual' | 'training' | null;

export interface SubscriptionState {
  isLoading: boolean;
  error: string | null;
  tier: SubscriptionTier;
  tierSource: TierSource;
  isSubscribed: boolean;
  isRealMember: boolean; // True if tier_source is stripe, guest, or manual (NOT training)
  productName: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  billingInterval: 'monthly' | 'yearly' | null;
  // Feature flags
  canAccessAllCourses: boolean;
  canAccessRotatingCourse: boolean;
  canDownloadMaterials: boolean;
  canAccessLiveWebinars: boolean;
  canAccessWebinarRecordings: boolean;
  canAccessLiederwerkstattLive: boolean;
  canAccessLiederwerkstattRecordings: boolean;
  canAccessCommunity: boolean;
  canGetCertificates: boolean;
  canGetAllCertificates: boolean;
  hasPrioritySupport: boolean;
  mikoRequestsPerMonth: number | 'unlimited';
  hasUnlimitedMiko: boolean;
  kreativpaketAccess: 'single' | 'modules-0-1' | 'full';
  // True after the first subscription check has completed (regardless of result)
  hasChecked: boolean;
}

// Cache is valid for 5 minutes - match backend
const CACHE_TTL_MS = 5 * 60 * 1000;

export function useSubscription() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [state, setState] = useState<SubscriptionState>({
    isLoading: true,
    error: null,
    tier: 'start',
    tierSource: null,
    isSubscribed: false,
    isRealMember: false,
    productName: null,
    subscriptionEnd: null,
    cancelAtPeriodEnd: false,
    billingInterval: null,
    hasChecked: false,
    ...getTierFeatures('start'),
  });

  // Track current tier via ref to detect changes without triggering re-renders
  const tierRef = useRef<SubscriptionTier>('start');

  // Prevent concurrent subscription checks (can happen on mount + auth events)
  const inFlightRef = useRef<Promise<void> | null>(null);

  // Cache admin role status to avoid repeated has_role RPC calls.
  const adminCacheRef = useRef<{ value: boolean | null; checkedAt: number }>({
    value: null,
    checkedAt: 0,
  });

  // Keep current path in a ref so our callbacks stay stable.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);
  
  // Track if initial check has been done
  const hasCheckedRef = useRef(false);
  
  // Store queryClient in ref to avoid dependency changes
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  // Helper to invalidate AccessContext cache after subscription changes
  const invalidateAccessCache = useCallback(() => {
    const path = pathnameRef.current || '/';
    const shouldInvalidate =
      path.startsWith('/mein-bereich') ||
      path.startsWith('/admin') ||
      path.startsWith('/checkout') ||
      path.startsWith('/abo-checkout') ||
      path.startsWith('/termin-checkout') ||
      path.startsWith('/kurs-vorschau');

    if (!shouldInvalidate) return;
    console.log('[useSubscription] Invalidating AccessContext cache');
    queryClientRef.current.invalidateQueries({ queryKey: ['global-access'] });
  }, []);

  const checkSubscription = useCallback((skipCache = false): Promise<void> => {
    if (inFlightRef.current) return inFlightRef.current;

    const p = (async () => {
      try {
        setState(prev => (prev.isLoading ? prev : ({ ...prev, isLoading: true, error: null })));

        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          adminCacheRef.current = { value: null, checkedAt: 0 };
          tierRef.current = 'start';
          setState({
            isLoading: false,
            error: null,
            tier: 'start',
            tierSource: null,
            isSubscribed: false,
            isRealMember: false,
            productName: null,
            subscriptionEnd: null,
            cancelAtPeriodEnd: false,
            billingInterval: null,
            hasChecked: true,
            ...getTierFeatures('start'),
          });
          return;
        }

        const now = Date.now();

        // Admin cache hit: avoid role RPCs.
        if (adminCacheRef.current.value === true && (now - adminCacheRef.current.checkedAt) < CACHE_TTL_MS) {
          console.log('[useSubscription] User is admin (cached), setting pro tier');
          if (tierRef.current !== 'pro') {
            console.log('[useSubscription] Tier changed from', tierRef.current, 'to pro (admin cached)');
            invalidateAccessCache();
          }
          tierRef.current = 'pro';
          setState({
            isLoading: false,
            error: null,
            tier: 'pro',
            tierSource: 'manual', // Admin = manual tier source
            isSubscribed: true,
            isRealMember: true, // Admins are always real members
            productName: SUBSCRIPTION_TIERS.pro.name,
            subscriptionEnd: null,
            cancelAtPeriodEnd: false,
            billingInterval: null,
            hasChecked: true,
            ...getTierFeatures('pro'),
          });
          return;
        }

        // Check if user is admin (super_admin or admin) - they always get pro tier
        const [superAdminCheck, adminCheck] = await Promise.all([
          supabase.rpc('has_role', { _user_id: session.user.id, _role: 'super_admin' }),
          supabase.rpc('has_role', { _user_id: session.user.id, _role: 'admin' }),
        ]);

        const isAdmin = superAdminCheck.data === true || adminCheck.data === true;
        adminCacheRef.current = { value: isAdmin, checkedAt: now };

        // If admin, set pro tier immediately without checking subscription
        if (isAdmin) {
          console.log('[useSubscription] User is admin, setting pro tier');

          // Check if tier changed - if so, invalidate AccessContext
          if (tierRef.current !== 'pro') {
            console.log('[useSubscription] Tier changed from', tierRef.current, 'to pro (admin)');
            invalidateAccessCache();
          }
          tierRef.current = 'pro';

          setState({
            isLoading: false,
            error: null,
            tier: 'pro',
            tierSource: 'manual', // Admin = manual tier source
            isSubscribed: true,
            isRealMember: true, // Admins are always real members
            productName: SUBSCRIPTION_TIERS.pro.name,
            subscriptionEnd: null,
            cancelAtPeriodEnd: false,
            billingInterval: null,
            hasChecked: true,
            ...getTierFeatures('pro'),
          });
          return;
        }

        // FAST PATH: Try to get cached data from user_usage table first
        const { data: cachedUsage, error: cacheError } = await supabase
          .from('user_usage')
          .select('subscription_tier, subscription_status, subscription_end, cancel_at_period_end, billing_interval, stripe_product_id, subscription_synced_at, tier_source, stripe_customer_id, stripe_subscription_id')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (!skipCache && !cacheError && cachedUsage?.subscription_synced_at) {
          const syncedAt = new Date(cachedUsage.subscription_synced_at).getTime();
          const isFresh = (now - syncedAt) < CACHE_TTL_MS;

          if (isFresh) {
            // Determine tier - prioritize subscription_tier field (covers manual overrides)
            let tier: SubscriptionTier = 'start';

            // First check subscription_tier field (includes manual overrides)
            if (cachedUsage.subscription_tier) {
              const tierValue = cachedUsage.subscription_tier as SubscriptionTier;
              if (['plus', 'pro'].includes(tierValue)) {
                tier = tierValue;
              }
            }

            // Then check stripe_product_id as fallback
            if (tier === 'start' && cachedUsage.stripe_product_id) {
              const detectedTier = getTierByProductId(cachedUsage.stripe_product_id);
              if (detectedTier) tier = detectedTier;
            }

            // User is "subscribed" if they have a paid tier (regardless of how they got it)
            const hasStripeSubscription = ['active', 'trialing', 'active_pending_payment'].includes(cachedUsage.subscription_status || '');
            const hasPaidTier = tier !== 'start';
            const isSubscribed = hasStripeSubscription || hasPaidTier;

            const billingInterval = cachedUsage.billing_interval === 'yearly' ? 'yearly'
              : cachedUsage.billing_interval === 'monthly' ? 'monthly'
                : null;

            // Get tier_source from cached data
            const tierSource = (cachedUsage.tier_source as TierSource) || null;

            // Zentrale Membership-Regel (siehe src/lib/access/membership.ts)
            const isRealMember = computeIsRealMember({
              tier,
              tierSource,
              stripeCustomerId: (cachedUsage as { stripe_customer_id?: string | null }).stripe_customer_id ?? null,
              stripeSubscriptionId: (cachedUsage as { stripe_subscription_id?: string | null }).stripe_subscription_id ?? null,
            });

            // Check if tier changed - if so, invalidate AccessContext
            if (tierRef.current !== tier) {
              console.log('[useSubscription] Tier changed from', tierRef.current, 'to', tier);
              invalidateAccessCache();
            }
            tierRef.current = tier;

            setState({
              isLoading: false,
              error: null,
              tier,
              tierSource,
              isSubscribed,
              isRealMember,
              productName: SUBSCRIPTION_TIERS[tier].name,
              subscriptionEnd: cachedUsage.subscription_end,
              cancelAtPeriodEnd: cachedUsage.cancel_at_period_end || false,
              billingInterval,
              hasChecked: true,
              ...getTierFeatures(tier),
            });

            console.log('[useSubscription] Using cached data', { tier, tierSource, isSubscribed, isRealMember, hasPaidTier, ageMs: now - syncedAt });
            return;
          }
        }

        // SLOW PATH: Cache is stale or missing, call edge function
        console.log('[useSubscription] Cache miss, calling edge function');

        const { data, error } = await supabase.functions.invoke('check-abo-subscription');

        if (error) {
          console.error('Error checking subscription:', error);
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: error.message,
          }));
          return;
        }

        if (!data?.subscribed) {
          // Logged in but no subscription = start tier
          tierRef.current = 'start';
          setState({
            isLoading: false,
            error: null,
            tier: 'start',
            tierSource: null,
            isSubscribed: false,
            isRealMember: false,
            productName: null,
            subscriptionEnd: null,
            cancelAtPeriodEnd: false,
            billingInterval: null,
            hasChecked: true,
            ...getTierFeatures('start'),
          });
          return;
        }

        // Determine tier from product ID
        let detectedTier: SubscriptionTier = 'start';

        if (data.product_id) {
          const tierFromProduct = getTierByProductId(data.product_id);
          if (tierFromProduct) {
            detectedTier = tierFromProduct;
          }
        }

        // Fallback: check tier field directly (map legacy 'plus' to 'pro')
        if (detectedTier === 'start' && data.tier) {
          if (data.tier === 'pro' || data.tier === 'plus') {
            detectedTier = 'pro';
          }
        }

        // Fallback: check product name
        if (detectedTier === 'start' && data.product_name) {
          const productNameLower = data.product_name.toLowerCase();
          if (productNameLower.includes('pro') || productNameLower.includes('plus') || productNameLower.includes('mitgliedschaft')) {
            detectedTier = 'pro';
          }
        }

        // Determine billing interval
        let billingInterval: 'monthly' | 'yearly' | null = null;
        if (data.plan === 'yearly' || data.interval === 'year') {
          billingInterval = 'yearly';
        } else if (data.plan === 'monthly' || data.interval === 'month') {
          billingInterval = 'monthly';
        }

        // Check if tier changed - if so, invalidate AccessContext
        if (tierRef.current !== detectedTier) {
          console.log('[useSubscription] Tier changed from', tierRef.current, 'to', detectedTier);
          invalidateAccessCache();
        }
        tierRef.current = detectedTier;

        // Get tier_source from API response
        const tierSource = (data.tier_source as TierSource) || null;
        
        // Zentrale Membership-Regel (siehe src/lib/access/membership.ts)
        const isRealMember = computeIsRealMember({
          tier: detectedTier,
          tierSource,
          stripeCustomerId: (data as { stripe_customer_id?: string | null }).stripe_customer_id ?? null,
          stripeSubscriptionId: (data as { subscription_id?: string | null }).subscription_id ?? null,
        });

        setState({
          isLoading: false,
          error: null,
          tier: detectedTier,
          tierSource,
          isSubscribed: true,
          isRealMember,
          productName: data.product_name || SUBSCRIPTION_TIERS[detectedTier].name,
          subscriptionEnd: data.subscription_end,
          cancelAtPeriodEnd: data.cancel_at_period_end || false,
          billingInterval,
          hasChecked: true,
          ...getTierFeatures(detectedTier),
        });
      } catch (err) {
        console.error('Subscription check error:', err);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        }));
      }
    })().finally(() => {
      inFlightRef.current = null;
    });

    inFlightRef.current = p;
    return p;
  }, [invalidateAccessCache]);

  // Initial check on mount - only run once
  useEffect(() => {
    if (!hasCheckedRef.current) {
      hasCheckedRef.current = true;
      void checkSubscription();
    }
  }, [checkSubscription]);

  // Re-check on auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        // Skip cache on login to get fresh data from Stripe
        void checkSubscription(true);
      } else if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        void checkSubscription();
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  // Periodic refresh every 5 minutes (match cache TTL)
  useEffect(() => {
    const interval = setInterval(() => {
      void checkSubscription();
    }, CACHE_TTL_MS);
    return () => clearInterval(interval);
  }, [checkSubscription]);

  const refreshSubscription = useCallback(() => {
    // Always skip cache for explicit refreshes to get real-time data
    // This is critical after registration where SIGNED_IN event may have
    // cached stale 'start' tier before Thinkific migration completed
    return checkSubscription(true);
  }, [checkSubscription]);

  return {
    ...state,
    refreshSubscription,
  };
}
